import { useEffect, useRef, useState } from 'react';
import { Users2 } from 'lucide-react';
import { useClassroomStore } from '../store';
import { getClassGroups, joinClassGroup } from '../classroomApi';
import type { ClassGroupsStudentView } from '../classroomApi';

// BP 22 / WIP-013 (design §6.4) — the student's group chip on the Class card
// header. Three states: nothing (no groups exist for this meeting, or
// self-select is off and the student has none), a fixed "Group C" chip once
// the student has one, or a "Pick your group" button when self-select is on
// and they don't. The student never types a label — picking one is the ONE
// tap this issue adds to the live path (everything else is server-stamped).
//
// D4 (design §12) — whether group-mates' names are shown at all — is an open
// decision; this chip does not show them, so nothing here has to unwind if
// that's answered "no".
interface GroupChipProps {
  sessionId: string;
}

export default function GroupChip({ sessionId }: GroupChipProps) {
  const myGroup = useClassroomStore((s) => s.myGroup);
  const refresh = useClassroomStore((s) => s.refresh);
  const [view, setView] = useState<ClassGroupsStudentView | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // One-off fetch, not a poll: the letter list and self_select flag change
  // only when the instructor edits them, and `myGroup` (already on the 5 s
  // poll via store.refresh) is what tells this chip to re-check after a join.
  useEffect(() => {
    let cancelled = false;
    getClassGroups(sessionId)
      .then((v) => { if (!cancelled) setView(v as ClassGroupsStudentView); })
      .catch(() => { /* no groups configured for this meeting — chip renders nothing */ });
    return () => { cancelled = true; };
  }, [sessionId, myGroup?.label]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (!view) return null;
  if (!myGroup && !view.self_select) return null;

  if (myGroup) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-ws-accent-quiet px-2 py-0.5 text-[11px] font-semibold text-ws-accent-ink">
        <Users2 size={10} /> Group {myGroup.label}
      </span>
    );
  }

  const handleJoin = async (label: string) => {
    setBusy(true);
    setError(null);
    try {
      await joinClassGroup(sessionId, label);
      await refresh();
      setOpen(false);
    } catch {
      setError('Could not join — try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-md bg-ws-accent-quiet px-2 py-0.5 text-[11px] font-semibold text-ws-accent-ink hover:opacity-80"
      >
        <Users2 size={10} /> Pick your group
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Pick your group"
          className="absolute left-0 top-full z-40 mt-1 flex max-w-[12rem] flex-wrap gap-1 rounded-lg border border-ws-border bg-ws-surface p-1.5 shadow-lg"
        >
          {view.groups.length === 0 && (
            <span className="px-1 py-0.5 text-[11px] text-ws-ink-3">No groups yet.</span>
          )}
          {view.groups.map((g) => (
            <button
              key={g.label}
              type="button"
              role="menuitem"
              disabled={busy}
              onClick={() => handleJoin(g.label)}
              className="rounded bg-ws-surface-2 px-2 py-1 text-[11px] font-medium text-ws-ink hover:bg-ws-accent-quiet disabled:opacity-40"
            >
              {g.label}
            </button>
          ))}
        </div>
      )}
      {error && <p role="alert" className="mt-1 text-[11px] text-ws-danger">{error}</p>}
    </div>
  );
}
